import React, { createContext, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

// Verification thresholds, fetched once at app load and shared app-wide so the
// directory chips, dashboard tiles, KPI labels, and per-record badges all agree
// (E77 — fixes the prior client/server drift where 180/365 were hardcoded in
// multiple places). Defaults mirror the backend seeds until the fetch resolves.
const DEFAULT_AGING   = 180;
const DEFAULT_OVERDUE = 365;

export interface VerificationSettings {
  agingDays:   number;
  overdueDays: number;
  isLoading:   boolean;
  refresh:     () => void;
}

const SettingsContext = createContext<VerificationSettings | undefined>(undefined);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn:  api.getSettings,
    staleTime: 1000 * 60 * 10, // settings change rarely
  });

  const parse = (raw: string | undefined, fallback: number): number => {
    const n = parseInt(raw ?? '', 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const value: VerificationSettings = {
    agingDays:   parse(data?.verification_aging_days,   DEFAULT_AGING),
    overdueDays: parse(data?.verification_overdue_days, DEFAULT_OVERDUE),
    isLoading,
    refresh:     () => { queryClient.invalidateQueries({ queryKey: ['settings'] }); },
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): VerificationSettings {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>');
  return ctx;
}

export default SettingsContext;
